// ============================================================
// 19. DiscountSummaryPage.tsx
// ============================================================
// src/features/reports/pages/DiscountSummaryPage.tsx

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { getBranches } from '@/features/settings/services/settingsApi';
import { getDiscountSummary, getExportUrl } from '../services/reportsApi';
import { ReportHeader } from '../components/ReportHeader';
import { ReportFilters } from '../components/ReportFilters';
import { SummaryCard } from '../components/SummaryCard';
import {
    Tag, DollarSign, Percent, TrendingDown,
    ShoppingCart, BarChart3, PieChart, Calendar
} from 'lucide-react';
import { motion } from 'framer-motion';

export function DiscountSummaryPage() {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';

    const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
    const [branchId, setBranchId] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [activeTab, setActiveTab] = useState<'reasons' | 'daily'>('reasons');

    const { data: branches } = useQuery({
        queryKey: ['branches-report', tenantId],
        queryFn: () => getBranches(tenantId),
        enabled: !!tenantId,
    });

    const params = {
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        branchId: branchId || undefined,
    };

    const { data, isLoading } = useQuery({
        queryKey: ['report-discount', tenantId, params],
        queryFn: () => getDiscountSummary(tenantId, params),
        enabled: !!tenantId,
    });

    const formatNaira = (val: string | number) =>
        `₦${parseFloat(String(val || '0')).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    const formatNumber = (val: string | number) => parseFloat(String(val || '0')).toLocaleString();

    const branchesList = (branches || []) as any[];

    const setQuickDate = (days: number) => {
        const end = new Date();
        const start = new Date();
        if (days === 0) start.setHours(0, 0, 0, 0);
        else start.setDate(start.getDate() - days);
        setDateRange({ startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] });
    };

    const handleExport = (format: 'pdf' | 'excel') => {
        const url = getExportUrl(tenantId, 'discount', format, params as any);
        window.open(url, '_blank');
    };

    const hasFilters = !!(dateRange.startDate || dateRange.endDate || branchId);
    const clearFilters = () => { setDateRange({ startDate: '', endDate: '' }); setBranchId(''); };

    const discountReasons = data?.discountReasons || [];
    const dailyData = data?.daily || [];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50/30">
            <ReportHeader
                title="Discount Summary"
                description="Discount analysis by type and period"
                icon={Tag}
                iconGradient="from-violet-500 to-purple-600"
                hasFilters={hasFilters}
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(!showFilters)}
                onExportPdf={() => handleExport('pdf')}
                onExportExcel={() => handleExport('excel')}
                branchName={branchesList.find(b => b.id === branchId)?.name}
            />

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                <ReportFilters
                    visible={showFilters}
                    dateRange={dateRange}
                    branchId={branchId}
                    branches={branchesList}
                    onDateRangeChange={setDateRange}
                    onBranchChange={setBranchId}
                    onClear={clearFilters}
                    onQuickDate={setQuickDate}
                />

                {isLoading ? (
                    <div className="flex justify-center py-20"><Spinner size="xl" /></div>
                ) : !data ? (
                    <EmptyState
                        icon={<Tag className="w-10 h-10" />}
                        title="No Discount Data"
                        description="No discounts found for the selected period."
                        size="lg"
                    />
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <SummaryCard
                                label="Total Discounts"
                                value={formatNumber(data.summary?.totalDiscounts || 0)}
                                icon={Tag}
                                gradient="from-violet-500 to-purple-600"
                            />
                            <SummaryCard
                                label="Total Amount"
                                value={formatNaira(data.summary?.totalDiscountAmount || 0)}
                                icon={DollarSign}
                                gradient="from-emerald-500 to-teal-600"
                            />
                            <SummaryCard
                                label="Avg Discount/Sale"
                                value={formatNaira(data.summary?.avgDiscountPerSale || 0)}
                                icon={Percent}
                                gradient="from-blue-500 to-indigo-600"
                            />
                            <SummaryCard
                                label="Discount Rate"
                                value={`${data.summary?.discountRate || 0}%`}
                                icon={TrendingDown}
                                gradient="from-amber-500 to-orange-600"
                                subtitle="of total revenue"
                            />
                        </div>

                        {/* Tab Navigation */}
                        <div className="flex gap-2 border-b border-slate-200 pb-2">
                            <button
                                onClick={() => setActiveTab('reasons')}
                                className={`px-4 py-2 text-sm font-medium rounded-lg capitalize ${activeTab === 'reasons' ? 'bg-violet-50 text-violet-700' : 'text-slate-500 hover:bg-slate-50'
                                    }`}
                            >
                                By Reason
                            </button>
                            <button
                                onClick={() => setActiveTab('daily')}
                                className={`px-4 py-2 text-sm font-medium rounded-lg capitalize ${activeTab === 'daily' ? 'bg-violet-50 text-violet-700' : 'text-slate-500 hover:bg-slate-50'
                                    }`}
                            >
                                Daily Breakdown
                            </button>
                        </div>

                        {activeTab === 'reasons' && (
                            <>
                                {/* Discount Reason Distribution */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {discountReasons.slice(0, 6).map((reason: any, i: number) => (
                                        <motion.div
                                            key={i}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: i * 0.05 }}
                                        >
                                            <Card hover>
                                                <CardContent className="p-4">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Tag className="w-4 h-4 text-violet-500" />
                                                        <span className="text-sm font-medium text-slate-700">{reason.reason}</span>
                                                    </div>
                                                    <p className="text-xl font-bold text-slate-900">{formatNaira(reason.amount)}</p>
                                                    <div className="flex items-center justify-between mt-2">
                                                        <span className="text-xs text-slate-500">{reason.count} sales</span>
                                                        <Badge variant="discount" size="sm">{reason.percentage}%</Badge>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        </motion.div>
                                    ))}
                                </div>

                                <Card className="overflow-hidden">
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-slate-100">
                                            <thead>
                                                <tr className="bg-slate-50/50">
                                                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Reason</th>
                                                    <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Count</th>
                                                    <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Amount</th>
                                                    <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Avg Discount</th>
                                                    <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Share</th>
                                                    <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Distribution</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-50">
                                                {discountReasons.map((reason: any, i: number) => (
                                                    <tr key={i} className="hover:bg-slate-50 transition-colors">
                                                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{reason.reason}</td>
                                                        <td className="px-6 py-4 text-center text-sm font-semibold">{reason.count}</td>
                                                        <td className="px-6 py-4 text-right text-sm font-bold text-violet-600">
                                                            {formatNaira(reason.amount)}
                                                        </td>
                                                        <td className="px-6 py-4 text-right text-sm text-slate-500">
                                                            {formatNaira(reason.count > 0 ? parseFloat(reason.amount) / reason.count : 0)}
                                                        </td>
                                                        <td className="px-6 py-4 text-center">
                                                            <Badge variant="discount" size="sm">{reason.percentage}%</Badge>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="w-full bg-slate-100 rounded-full h-2">
                                                                <div
                                                                    className="h-2 rounded-full bg-gradient-to-r from-violet-500 to-purple-600"
                                                                    style={{ width: `${reason.percentage}%` }}
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </Card>
                            </>
                        )}

                        {activeTab === 'daily' && (
                            <Card className="overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-slate-100">
                                        <thead>
                                            <tr className="bg-slate-50/50">
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Date</th>
                                                <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Discounted Sales</th>
                                                <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Revenue</th>
                                                <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Discount Amount</th>
                                                <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Discount Rate</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {dailyData.map((day: any, i: number) => (
                                                <tr key={i} className="hover:bg-slate-50 transition-colors">
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-2">
                                                            <Calendar className="w-3.5 h-3.5 text-slate-400" />
                                                            <span className="text-sm font-medium text-slate-900">
                                                                {new Date(day.date).toLocaleDateString('en-NG', { weekday: 'short', day: 'numeric', month: 'short' })}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-center text-sm">{day.count}</td>
                                                    <td className="px-6 py-4 text-right text-sm font-bold text-slate-900">
                                                        {formatNaira(day.revenue)}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm font-bold text-violet-600">
                                                        {formatNaira(day.amount)}
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <Badge variant="discount" size="sm">{day.discountRate}%</Badge>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Card>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}